import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Component, TemplateRef, ViewChild, WritableSignal, DestroyRef, computed, inject, input, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { finalize, firstValueFrom, of, tap, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../../authentication/authentication.service';
import { FirebaseEventService } from '../../authentication/firebase.event.service';
import { BillingChargeService, CreditAction } from '../../core/billing-charge.service';
import { CableCommentsDto, CableDto, CableSummaryDto, CableUpdateRequestDto, MceApiService, ProjectDto } from '../../core/mceapi.client.generated';
import { RecentReport, RecentReportsService } from '../../core/recent-reports.service';
import { Report, ReportsService } from '../../core/reports.service';
import { ProjectHeaderComponent } from '../../projects/project-header/project-header.component';
import { MceDataTableComponent } from '../../shared/components/mce-data-table/mce-data-table.component';
import { MceRseponsiveActionButtonComponent } from '../../shared/components/mce-responsive-action-button/mce-responsive-action-button.component';
import { DialogService } from '../../shared/dialogs/dialog.service';
import { Option, none } from '../../shared/functions/functional';
import '../../shared/functions/number.extensions';
import { CableEditComponent } from '../cables-edit/cable-edit.component';
import { CableEditService } from '../cables-edit/cable-edit.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { InfoMessageComponent } from '../../shared/components/info-message/info-message.component';
import { hasRole, Role } from '../../shared/enums/role';

interface DataTable {
  headers: { [key: string]: string; };
  displayedColumns: string[];
  templates: { [key: string]: TemplateRef<any>; };
  showSelect: boolean;
  data: CableSummaryDto[];
}

enum DisplayMode { DEFAULT = 'DEFAULT', ADD = 'ADD', EDIT = 'EDIT', }


@Component({
  selector: 'mce-cable-list',
  imports: [CommonModule, CableEditComponent, MatToolbarModule, MatIconModule, MceDataTableComponent,
    MatButtonModule, MatProgressBarModule, MatProgressSpinnerModule, ProjectHeaderComponent,
    MatMenuModule, MatDividerModule, MceRseponsiveActionButtonComponent, MatTooltipModule,
    ClipboardModule, InfoMessageComponent, MatSnackBarModule, RouterLink],
  templateUrl: './cable-list.component.html',
})
export class CableListComponent {

  project = input<ProjectDto>({});

  private readonly cableEditor = viewChild(CableEditComponent);
  readonly hasUnsavedChanges = computed(() => this.cableEditor()?.form().dirty() ?? false);

  @ViewChild('rowActions', { static: true }) rowActions!: TemplateRef<any>;

  report = Report;

  displayMode = signal<DisplayMode>(DisplayMode.DEFAULT);
  editCable = signal<CableDto>({});
  editComments = signal<Option<CableCommentsDto>>(none);
  selectedRows = signal<CableSummaryDto[]>([]);
  processing = signal(false);

  private authenticationService = inject(AuthenticationService);
  private billingChargeService = inject(BillingChargeService);

  protected readonly isSharedBillingMember = computed(() =>
    hasRole(this.authenticationService.currentUserRoles(), Role.SharedBillingMember));
  private cableEditService = inject(CableEditService);
  private mceApiService = inject(MceApiService);
  private firebaseEventService = inject(FirebaseEventService);
  private dialogService = inject(DialogService);
  private reportsService = inject(ReportsService);
  private http = inject(HttpClient);
  private readonly recentReportsService = inject(RecentReportsService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly recentReports = computed(() => this.recentReportsService.reportsFor(
    this.authenticationService.currentUserState()?.user?.uid, this.project().uid));

  refreshRecentReports(): void {
    this.recentReportsService.refresh();
  }

  openRecentReport(entry: RecentReport): void {
    this.refreshRecentReports();
    // Recheck the active account/project and expiry even if the menu or snackbar is still open.
    const saved = this.recentReports().find(report => report.url === entry.url);
    if (saved) window.open(saved.url, '_blank', 'noopener');
  }

  private cablesResource = rxResource({
    defaultValue: [] as CableSummaryDto[],
    params: () => ({ project: this.project() }),
    stream: ({ params }) => params.project.uid
      ? this.mceApiService.v1CablesProjectSummary(params.project.uid)
      : of([] as CableSummaryDto[])
  });
  cables = computed<WritableSignal<CableSummaryDto[]>>(() => this.cablesResource.value);

  protected readonly dataTable = computed<DataTable>(() => {
    return {
      headers: {
        'designation': 'Designation',
        'description': 'Description',
        'supply': 'Supply',
        'load': 'Ib',
        'actions': ''
      },
      displayedColumns: ['designation', 'description', 'supply', 'load', 'actions'],
      templates: {
        'actions': this.rowActions
      },
      showSelect: true,
      data: this.cables()(),
    };
  });

  protected readonly viewModel = computed(() => {
    const roles = this.authenticationService.currentUserRoles();
    const freeTier = hasRole(roles, Role.FreeTier);
    const proTier = hasRole(roles, Role.ProTier);
    const freeCreditsUser = hasRole(roles, Role.FreeCreditsUser);
    const cableCount = this.cables()().length;
    const selectedCableCount = this.selectedRows().length;
    const freePlanRestrictionsApply = freeTier && !proTier && !freeCreditsUser && !this.isSharedBillingMember();
    const freePlanLimitReached = freePlanRestrictionsApply && cableCount === 3;
    const freePlanLimitExceeded = freePlanRestrictionsApply && cableCount > 3;
    const canCreateCable = (freeTier && cableCount < 3) || proTier || freeCreditsUser || this.isSharedBillingMember();
    const cableCreationDisabled = !canCreateCable;
    const selectedCableCopyWouldExceedLimit = freePlanRestrictionsApply && cableCount + selectedCableCount > 3;
    const copySelectedDisabled = selectedCableCount === 0 || cableCreationDisabled || selectedCableCopyWouldExceedLimit;
    const disableReportsButton = cableCount === 0 && this.recentReports().length === 0;

    return {

      dataTable: this.dataTable(),

      editCable: this.editCable(),
      editComments: this.editComments(),

      disableDelete: !this.selectedRows().length,
      copySelectedDisabled,
      moveSelectedDisabled: !this.selectedRows().length || freePlanLimitExceeded,

      showLoading: this.cablesResource.isLoading(),
      showSpinner: this.processing(),

      showDatatable:
        !this.cablesResource.isLoading() && (this.displayMode() === DisplayMode.DEFAULT),

      showProjectHeader: this.displayMode() === DisplayMode.DEFAULT,

      showEditForm:
        ((this.displayMode() === DisplayMode.ADD) || (this.displayMode() === DisplayMode.EDIT))
        && this.editCable().uid !== undefined,

      cableCreationDisabled,
      freePlanRestrictionsApply,
      freePlanLimitReached,
      freePlanLimitExceeded,
      disableReportsButton,

      showDebugPanel: window.location.href.toLowerCase().indexOf('localhost') > 0

    };
  });

  protected get vm() { return this.viewModel(); }

  private requireProjectUid(): string {
    const projectUid = this.project().uid;
    if (!projectUid) throw new Error('Project DTO is missing its UID.');
    return projectUid;
  }

  private requireCableUid(cable: CableSummaryDto): string {
    if (!cable.uid) throw new Error('Cable DTO is missing its UID.');
    return cable.uid;
  }

  private requireCableUids(cables: CableSummaryDto[]): string[] {
    return cables.map(cable => this.requireCableUid(cable));
  }


  async addCable() {

    if (this.vm.cableCreationDisabled) return;

    try {
      this.processing.set(true);

      const chargeQuote = await firstValueFrom(this.billingChargeService.getQuote({
        action: CreditAction.CableAdd,
      }));

      const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
      if (!canProceed) return;

      const createCable = await this.cableEditService.createCable(this.requireProjectUid());
      if (!createCable.cable) throw new Error('Create cable response did not contain a cable.');

      this.editCable.set(createCable.cable);
      this.editComments.set(none);
      this.displayMode.set(DisplayMode.ADD);
    } finally {
      this.processing.set(false);
    }
  }

  async onEditCable(cable: CableSummaryDto) {
    if (this.vm.freePlanLimitExceeded) return;

    const cableUid = this.requireCableUid(cable);

    try {
      this.processing.set(true);

      const [getCable, getComments] = await Promise.all([
        this.cableEditService.getCable(cableUid),
        this.cableEditService.getCableComments(cableUid),
      ]);

      this.editCable.set(getCable);
      this.editComments.set(getComments);
      this.displayMode.set(DisplayMode.EDIT);
    } finally {
      this.firebaseEventService.logEditCableStartedEvent(
        this.authenticationService.currentUser(), this.editCable().projectUid ?? '', this.editCable().uid ?? "");
      this.processing.set(false);
    }
  }

  async onCopyCable(cable: CableSummaryDto) {
    if (this.vm.cableCreationDisabled) return;

    try {
      this.processing.set(true);

      const chargeQuote = await firstValueFrom(this.billingChargeService.getQuote({
        action: CreditAction.CableCopy,
      }));

      const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
      if (!canProceed) return;

      const copiedCable = await this.cableEditService.copyCable(cable);

      await firstValueFrom(this.billingChargeService.applyCharge(chargeQuote));

      this.cables().set([copiedCable, ...this.cables()()]);
    } finally {
      this.processing.set(false);
    }
  }

  async onMoveCable(cable: CableSummaryDto) {

    if (this.vm.freePlanLimitExceeded) return;

    let result = await this.dialogService.moveCableProjectSelectAsync(this.requireProjectUid());
    if (!result) return;
    try {
      this.processing.set(true);
      const movedCable = await this.cableEditService.moveCable(cable, result.toProjectUid);
      this.cables().set(this.cables()().filter(c => c.uid !== movedCable.uid));
    } finally {
      this.processing.set(false);
    }
  }

  async onDeleteCable(cable: CableSummaryDto) {
    try {
      this.processing.set(true);
      await this.cableEditService.deleteCable(cable);
      this.cables().set(this.cables()().filter(c => c.uid !== cable.uid));
    } finally {
      this.processing.set(false);
    }
  }

  async onCopyCables(cables: CableSummaryDto[]) {
    const copyWouldExceedLimit = this.vm.freePlanRestrictionsApply
      && this.cables()().length + cables.length > 3;
    if (!cables.length || this.vm.cableCreationDisabled || copyWouldExceedLimit) return;

    try {
      this.processing.set(true);

      const chargeQuote = await firstValueFrom(this.billingChargeService.getQuote({
        action: CreditAction.CableCopyMultiple,
        cableCount: cables.length,
      }));

      const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
      if (!canProceed) return;

      const totalCredits = chargeQuote.quote?.cost ?? 0;
      const copySummary = this.isSharedBillingMember()
        ? `You are copying <b>${cables.length}</b> cables.`
        : `Copying <b>${cables.length}</b> cables will charge <b>${totalCredits}</b> credits.`;
      const confirmed = await this.dialogService.confirmAsync(
        'Confirm Copy Selected Cables',
        `${copySummary}<br />Are you sure you want to continue?`
      );
      if (!confirmed) return;

      const copiedCables = await this.cableEditService.copyCables(cables);

      await firstValueFrom(this.billingChargeService.applyCharge(chargeQuote));

      this.cables().set([...copiedCables, ...this.cables()()]);
    } finally {
      this.selectedRows.set([]);
      this.processing.set(false);
    }
  }

  async onMoveCables(cables: CableSummaryDto[]) {

    if (!cables.length || this.vm.freePlanLimitExceeded) return;

    let result = await this.dialogService.moveCableProjectSelectAsync(this.requireProjectUid());
    if (!result) return;

    try {
      this.processing.set(true);
      await this.cableEditService.moveCables(cables, result.toProjectUid);
      this.cables().set(this.cables()().filter(c => !cables.find(x => x.uid === c.uid)));
    } finally {
      this.selectedRows.set([]);
      this.processing.set(false);
    }

  }

  async onDeleteCables(cables: CableSummaryDto[]) {

    if (!cables.length) return;

    let confirmed = await this.dialogService.confirmAsync(
      'Confirm Delete Selected Cables',
      `Deleting <b>${cables.length}</b> cables.<br />Are you sure you want to delete these cables?
      `);
    if (!confirmed) return;

    try {
      this.processing.set(true);
      await this.cableEditService.deleteCables(cables);
      this.cables().set(this.cables()().filter(c => !cables.find(x => x.uid === c.uid)));
    } finally {
      this.selectedRows.set([]);
      this.processing.set(false);
    }
  }

  async onDoReports(report: Report) {
    const generatedAt = Date.now();
    const userUid = this.authenticationService.currentUserState()?.user?.uid;
    const projectUid = this.requireProjectUid();
    const ids = this.requireCableUids(
      this.selectedRows().length === 0 ? this.cables()() : this.selectedRows());
    try {
      this.processing.set(true);
      const url = await this.reportsService.doChargedReport(report, ids);
      if (!url?.value) return;

      if (userUid) {
        this.recentReportsService.add({
          userUid, projectUid, report, name: this.reportsService.reportToDisplayName(report), url: url.value, generatedAt,
        });
      }

      const entry = this.recentReportsService.reportsFor(userUid, projectUid)
        .find(saved => saved.url === url.value);
      if (entry) {
        this.snackBar.open('Report ready', 'Open', { duration: 10000 })
          .onAction().pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.openRecentReport(entry));
      }

      window.open(url.value, '_blank', 'noopener');
      this.firebaseEventService.logReportEvent(
        this.authenticationService.currentUser(),
        this.reportsService.reportToString(report)
      );
    } finally {
      this.processing.set(false);
    }
  }

  async onDoExport() {
    const ids = this.requireCableUids(
      this.selectedRows().length === 0 ? this.cables()() : this.selectedRows());

    try {
      this.processing.set(true);

      const chargeQuote = await firstValueFrom(this.billingChargeService.getQuote({
        action: CreditAction.ExportData,
      }));

      const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
      if (!canProceed) return;

      const blob = await firstValueFrom(this.http.post(
        `${environment.mceApiUrl}/v1/reports/export`,
        { projectUid: this.project().uid, cableUids: ids },
        { responseType: 'blob' }
      ));

      await firstValueFrom(this.billingChargeService.applyCharge(chargeQuote));

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export.json';
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      this.processing.set(false);
    }
  }

  async onEditSave(request: CableUpdateRequestDto) {

    if (this.displayMode() === DisplayMode.ADD && this.vm.cableCreationDisabled) return;

    let updateCables = (cables: CableSummaryDto[], updatedCable: CableSummaryDto) =>
      cables.find(c => c.uid === updatedCable.uid)
        ? cables.map(c => c.uid === updatedCable.uid ? updatedCable : c) // update existing cable
        : [updatedCable, ...cables]; // add new cable

    try {
      this.processing.set(true);

      const displayMode = this.displayMode();
      const chargeQuote = displayMode === DisplayMode.ADD
        ? await firstValueFrom(this.billingChargeService.getQuote({
          action: CreditAction.CableAdd,
        }))
        : undefined;

      if (chargeQuote) {
        const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
        if (!canProceed) return;
      }

      let updatedCable =
        displayMode === DisplayMode.EDIT
          ? await this.cableEditService.updateCable(request)
          : await this.cableEditService.saveNewCable(request);

      if (chargeQuote) {
        await firstValueFrom(this.billingChargeService.applyCharge(chargeQuote));
      }

      this.cables().set(updateCables(this.cables()(), updatedCable));
      this.displayMode.set(DisplayMode.DEFAULT);
    } finally {
      this.processing.set(false);
    }

  }

  onCancel() { this.displayMode.set(DisplayMode.DEFAULT); }


}
